import { BacktesterTransactionRow } from '../../../../../providers/backtesting/api/backtester-config-api.types';
import { TransactionDataType } from '../../../../../providers/portfolio-performance/transactions/transactions.types';

// A fully-blank TransactionDataType (every field is an empty string). The PP
// transactions table already tolerates partial rows — OKX and Deribit each
// leave the other venue's fields blank — so a backtest row just fills what it
// has and leaves the rest empty (lossy in our favour, per the reuse plan).
const blankRow = (): TransactionDataType => ({
  AccountID: '',
  AccountName: '',
  amount: '',
  balance: '',
  cashflow: '',
  change: '',
  commission: '',
  currency: '',
  equity: '',
  event_type: '',
  fee_balance: '',
  id: '',
  index_price: '',
  info: '',
  instrument_name: '',
  instrument_type: '',
  interest_pl: '',
  mark_price: '',
  order_id: '',
  position: '',
  price: '',
  price_currency: '',
  session_rpl: '',
  session_upl: '',
  side: '',
  timestamp: '',
  total_interest_pl: '',
  trade_id: '',
  type: '',
  user_id: '',
  user_role: '',
  user_seq: '',
  username: '',
});

const str = (value: unknown): string => (value == null ? '' : String(value));

// Direction of a fill from the signed backtest `amount` — the only side signal
// a backtest row carries. Zero/unparseable => blank (the table renders '-').
const sideFromAmount = (amount: unknown): string => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return '';
  return n > 0 ? 'buy' : 'sell';
};

// The backtest-specific fields the transactions table reads under variant
// 'backtest' (the engine's Transaction struct has no scalar price — it carries
// spot / future / volatility / premium / fee_rate instead).
export interface BacktestTransactionTableRow extends TransactionDataType {
  spot: string;
  future: string;
  volatility: string;
  premium: string;
  fee_rate: string;
}

// Maps a backtest scenario's transactions (the engine's Transaction struct) onto
// the shared transactions table's row shape. The table's 'backtest' variant reads
// the struct fields directly:
//   action       -> event_type   (Action column)
//   amount sign  -> side          (direction; amount is the option Notional)
//   instrument   -> instrument_name
//   amount       -> amount
//   fee          -> commission    (Fee column; used as-is, positive = cost)
//   fee_rate     -> fee_rate      (Fee Rate column, shown in bps)
//   spot/future  -> spot/future   (Exec Price / Rate: future for perps, spot for
//                                  spot trades; options have no scalar price)
//   volatility   -> volatility    (Vol column, options only)
//   premium      -> premium       (Premium column)
//   token        -> currency      (fee unit)
//   timestamp    -> timestamp     (Date column)
// price_currency is left blank so values render as plain numbers (formatNumber)
// rather than being forced into a USD "$" format they aren't denominated in.
export const buildTransactionRows = (
  transactions: BacktesterTransactionRow[],
  token: string,
): BacktestTransactionTableRow[] =>
  transactions.map((tx) => {
    const row = tx as Record<string, unknown>;
    return {
      ...blankRow(),
      timestamp: str(tx.timestamp),
      event_type: str(tx.action),
      instrument_name: str(row.instrument),
      side: sideFromAmount(row.amount),
      amount: str(row.amount),
      commission: str(row.fee),
      currency: token,
      spot: str(row.spot),
      future: str(row.future),
      volatility: str(row.volatility),
      premium: str(row.premium),
      fee_rate: str(row.fee_rate),
    };
  });
